import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Card, 
    CardContent, 
    Button, 
    Typography, 
    Alert,
    Container,
    CircularProgress,
    TextField
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function PasswordResetSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [codeVerified, setCodeVerified] = useState(false);
    const [passwordResetComplete, setPasswordResetComplete] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isResetting, setIsResetting] = useState(false);

    const oobCode = searchParams.get('oobCode');
    const mode = searchParams.get('mode');

    useEffect(() => {
        if (mode === 'resetPassword' && oobCode) {
            verifyPasswordResetCode(auth, oobCode)
                .then(() => {
                    setCodeVerified(true);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Reset code verification failed:', error);
                    setError('Ungültiger oder abgelaufener Reset-Code.');
                    setLoading(false);
                });
        } else {
            setError('Ungültige Reset-URL.');
            setLoading(false);
        }
    }, [oobCode, mode]);

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setIsResetting(true);
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwörter stimmen nicht überein.');
            setIsResetting(false);
            return;
        }

        if (newPassword.length < 6) {
            setError('Passwort muss mindestens 6 Zeichen lang sein.');
            setIsResetting(false);
            return;
        }

        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            setPasswordResetComplete(true);
        } catch (error) {
            console.error('Password reset failed:', error);
            setError('Fehler beim Zurücksetzen des Passworts. Bitte versuchen Sie es erneut.');
            setIsResetting(false);
        }
    };

    if (loading) {
        return (
            <Container component="main" maxWidth="sm">
                <Box sx={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && !codeVerified) {
        return (
            <Container component="main" maxWidth="sm">
                <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Card sx={{ width: '100%', maxWidth: 400 }}>
                        <CardContent>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate('/password-reset')}
                            >
                                Neuen Reset-Link anfordern
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        );
    }

    if (passwordResetComplete) {
        return (
            <Container component="main" maxWidth="sm">
                <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Card sx={{ width: '100%', maxWidth: 400 }}>
                        <CardContent>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                                <Typography variant="h5" gutterBottom>
                                    Passwort erfolgreich zurückgesetzt!
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
                                </Typography>
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate('/')}
                            >
                                Zur Anmeldung
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="sm">
            <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Card sx={{ width: '100%', maxWidth: 400 }}>
                    <CardContent>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <Typography variant="h5" gutterBottom>
                                Passwort zurücksetzen
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Geben Sie Ihr neues Passwort ein.
                            </Typography>
                        </Box>

                        <Box component="form" onSubmit={handlePasswordReset}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="newPassword"
                                label="Neues Passwort"
                                type="password"
                                id="newPassword"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isResetting}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmPassword"
                                label="Passwort bestätigen"
                                type="password"
                                id="confirmPassword"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isResetting}
                            />

                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={isResetting || !newPassword || !confirmPassword}
                            >
                                {isResetting ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    'Passwort zurücksetzen'
                                )}
                            </Button>

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Button
                                    variant="text"
                                    onClick={() => navigate('/')}
                                    disabled={isResetting}
                                >
                                    Zurück zur Anmeldung
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
}
